import type {
  Assessment,
  ModelTier,
  SignalReason,
} from '../domain/types'
import {
  applyOutcomeLensPolicy,
  withAssessmentTier,
  type OutcomeLensResult,
} from './outcomeLenses'

const DEEP_PREVIEW_RULES = [
  {
    id: 'root-cause-uncertainty',
    pattern:
      /\b(?:root cause|intermittent|non-deterministic|deadlock|race condition|occasionally double-charged|occasionally double charged)\b/i,
  },
  {
    id: 'conflicting-architecture-evidence',
    pattern:
      /\b(?:reconcile conflicting|cross-provider model router|trust-boundary failures?|competing architectures?)\b/i,
  },
  {
    id: 'long-horizon-risk',
    pattern:
      /\b(?:multi-system data migration|dependent phases|rollback gates?|live-verify every public route)\b/i,
  },
  {
    id: 'production-harm',
    pattern:
      /\bproduction\b[\s\S]*\b(?:payments?|double-charged|authentication|security|migration)\b/i,
  },
] as const

const ACTIONABLE_PROMPT =
  /\b(?:add|audit|build|calculate|call|classify|compare|create|debug|design|diagnose|draft|extract|find|generate|implement|investigate|map|plan|read|recommend|research|return|review|rewrite|summarize|translate|update|verify|write)\b/i
const VAGUE_PROMPT =
  /^(?:please\s+)?(?:help|make (?:it|this) better|fix it|do this|what should i do)[.!?]?$/i

export type ConservativePreview = {
  route: Extract<ModelTier, 'standard' | 'deep'> | null
  clarificationCandidate: boolean
  matchedSignalIds: string[]
}

/**
 * Provides the deliberately high-biased route shown before semantic setup
 * finishes. It never recommends Direct and therefore trades efficiency for
 * lower under-routing risk.
 */
export function conservativeLexicalPreview(
  prompt: string,
): ConservativePreview {
  const normalizedPrompt = prompt.trim().replace(/\s+/g, ' ')
  if (
    normalizedPrompt.length === 0 ||
    VAGUE_PROMPT.test(normalizedPrompt) ||
    !ACTIONABLE_PROMPT.test(normalizedPrompt)
  ) {
    return {
      route: null,
      clarificationCandidate: true,
      matchedSignalIds: [],
    }
  }

  const matchedSignalIds = DEEP_PREVIEW_RULES.filter(
    ({ pattern }) => pattern.test(normalizedPrompt),
  ).map(({ id }) => id)

  return {
    route: matchedSignalIds.length > 0 ? 'deep' : 'standard',
    clarificationCandidate: false,
    matchedSignalIds,
  }
}

const TIER_RANK: Record<ModelTier, number> = {
  direct: 0,
  standard: 1,
  deep: 2,
}
const TIERS: ModelTier[] = ['direct', 'standard', 'deep']

function previewReason(): SignalReason {
  return {
    id: 'semantic-setup-conservative-preview',
    label: 'Conservative setup preview',
    detail:
      'Uses a higher-capability floor until the local semantic check finishes.',
    impact: 'risk',
  }
}

export function conservativePreviewAssessment(
  prompt: string,
  assessment: Assessment,
): Assessment {
  const preview = conservativeLexicalPreview(prompt)
  const previewFloor = preview.route ?? 'standard'
  const tier =
    TIERS[
      Math.max(
        TIER_RANK[assessment.tier],
        TIER_RANK[previewFloor],
      )
    ] ?? assessment.tier

  return withAssessmentTier(
    assessment,
    tier,
    previewReason(),
  )
}

export function conservativePreviewOutcomeLenses(
  prompt: string,
  outcomeLenses: OutcomeLensResult,
): OutcomeLensResult {
  const previewAssessment = conservativePreviewAssessment(
    prompt,
    outcomeLenses.bestFit.assessment,
  )

  return applyOutcomeLensPolicy(
    outcomeLenses.bestFit.workItem,
    outcomeLenses.inferredContext,
    previewAssessment,
    {
      minimumFastestTier: previewAssessment.tier,
    },
  )
}
